import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import {
  BookOpen,
  Boxes,
  GitPullRequestArrow,
  LayoutDashboard,
  Plus,
  Search,
  Ticket as TicketIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { agentApi, type AgentTicket } from '../../agent/api'
import { useAuthz } from '../../agent/authz'
import { cn } from '../../lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/primitives'

/** Ctrl/Cmd+K global search.
 *
 * Two server-side searches exist and both are here: the queue's `q` matches a
 * ticket by ref, title or requester email, and the CMDB's `q` matches a
 * configuration item by name or class. The comment here used to say tickets
 * were the only one, which the full-site audit found was untrue — `/cis/?q=`
 * had been filtering since the CMDB shipped.
 *
 * Users, organisations and knowledge are still destinations rather than
 * results: no endpoint searches a person, and the agent KB list takes no `q`
 * at all (the portal's does). Those are real gaps and stay named as such.
 *
 * Both searches are gated on the permission that guards their own screen. An
 * agent without `asset.view` would get a 403 per keystroke otherwise, and a
 * palette that quietly 403s is worse than one that shows less.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate()
  const [term, setTerm] = useState('')

  const { can } = useAuthz()
  const searchable = open && term.trim().length >= 2

  // Only ask the server once the term is worth a round trip.
  const { data: tickets = [], isFetching } = useQuery({
    queryKey: ['command-search', term],
    queryFn: () => agentApi.get<AgentTicket[]>(`/tickets/?q=${encodeURIComponent(term)}`),
    enabled: searchable && can('ticket.view_all'),
    staleTime: 10_000,
  })

  const { data: cis = [], isFetching: cisFetching } = useQuery({
    queryKey: ['command-search-cis', term],
    queryFn: () =>
      agentApi.get<{ id: number; name: string; ci_class: string }[]>(
        `/cis/?q=${encodeURIComponent(term)}`,
      ),
    enabled: searchable && can('asset.view'),
    staleTime: 10_000,
  })

  useEffect(() => {
    if (!open) setTerm('')
  }, [open])

  function go(path: string) {
    onOpenChange(false)
    navigate(path)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-hidden p-0" hideClose>
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">
          Search tickets by reference, subject or requester, assets by name, or jump to a
          section.
        </DialogDescription>

        <Command shouldFilter={false} loop>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <Command.Input
              value={term}
              onValueChange={setTerm}
              autoFocus
              placeholder="Search tickets and assets, or jump to…"
              className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {(isFetching || cisFetching) && (
              <span className="text-xs text-muted-foreground">…</span>
            )}
          </div>

          <Command.List className="max-h-80 overflow-y-auto scrollbar-thin p-1.5">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              {term.trim().length < 2 ? 'Type to search tickets and assets.' : 'Nothing matched.'}
            </Command.Empty>

            {tickets.length > 0 && (
              <Command.Group
                heading="Tickets"
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {tickets.slice(0, 8).map((ticket) => (
                  <Item
                    key={ticket.ref}
                    icon={<TicketIcon />}
                    onSelect={() => go(`/agent/tickets/${ticket.ref}`)}
                  >
                    <span className="font-mono text-xs text-muted-foreground">{ticket.ref}</span>
                    <span className="truncate">{ticket.title}</span>
                  </Item>
                ))}
              </Command.Group>
            )}

            {cis.length > 0 && (
              <Command.Group
                heading="Assets"
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {cis.slice(0, 5).map((ci) => (
                  <Item
                    key={ci.id}
                    icon={<Boxes />}
                    onSelect={() => go(`/agent/assets/${ci.id}`)}
                  >
                    <span className="truncate">{ci.name}</span>
                    <span className="text-xs text-muted-foreground">{ci.ci_class}</span>
                  </Item>
                ))}
              </Command.Group>
            )}

            <Command.Group
              heading="Go to"
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground"
            >
              <Item icon={<LayoutDashboard />} onSelect={() => go('/agent')}>
                Dashboard
              </Item>
              <Item icon={<TicketIcon />} onSelect={() => go('/agent/tickets')}>
                All tickets
              </Item>
              <Item icon={<GitPullRequestArrow />} onSelect={() => go('/agent/changes')}>
                Changes
              </Item>
              <Item icon={<BookOpen />} onSelect={() => go('/agent/kb')}>
                Knowledge base
              </Item>
              <Item icon={<Plus />} onSelect={() => go('/new')}>
                New ticket
              </Item>
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  )
}

function Item({
  icon,
  children,
  onSelect,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onSelect: () => void
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-default select-none items-center gap-2.5 rounded-md px-2 py-2 text-sm outline-none',
        'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
        '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground',
      )}
    >
      {icon}
      {children}
    </Command.Item>
  )
}

/** Binds Ctrl/Cmd+K. Returned state drives the palette. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((previous) => !previous)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return { open, setOpen }
}
